import { useState, useEffect } from 'react';
import { Modal, Form, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import LoadingOverlay from 'react-loading-overlay';
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { FaCheck } from "react-icons/fa6";
import { IoMdClose } from "react-icons/io";
import { useSelector } from 'react-redux';
import _ from 'lodash';

// Custom Component
import ErrorText from '../TextField/ErrorText'
import { PrimaryButton } from '../Button';
import SuccessAlert from '../Alert/SuccessAlert';
import ErrorAlert from '../Alert/ErrorAlert';

// API Service
import { allTournamentService } from '../../service/tournament.service';
import { addTShirtService } from '../../service/tshirt.service';

// Redux-Functions
import { selectUser } from "../../redux/userSlice";

const AddTShirtModal = (props) => {
    const user = useSelector(selectUser);

    const [tournamentList, setTournamentList] = useState([])
    const [loading, setLoading] = useState(false);
    LoadingOverlay.propTypes = undefined

    const { register, control, watch, reset, handleSubmit, formState: { errors } } = useForm({
        defaultValues: {
            tshirt_details: [
                {
                    size: "",
                    quantity: ""
                }
            ]
        },
        mode: "all"
    })
    const watchTournament = watch("tournament_id")

    const { fields, append, remove } = useFieldArray({
        control,
        name: "tshirt_details"
    });

    /**
        * @function fetchTournamentList
        * @params
        * @description fetch the list of tournament
    */
    const fetchTournamentList = async () => {
        try {
            const result = await allTournamentService({
                paginate: 0,
                page: '',
                perPage: '',
                keyword: ''
            });
            if (result?.data?.status) {
                setTournamentList(result?.data?.data?.tournament_lists)
            } else {
                ErrorAlert(result?.response?.data?.message)
            }
        } catch (error) {
            ErrorAlert(error)
        }
    }

    useEffect(() => {
        if (user?.type === 'organizer') {
            fetchTournamentList()
        }
        // eslint-disable-next-line
    }, [])

    /**
        * @function onSubmit
        * @params formdata
        * @description used to add t-shirt 
    */
    const onSubmit = async (formdata) => {
        try {
            const data = {}
            data.tshirt_name = formdata?.tshirt_name
            data.is_select_all_tournament = _.isEqual(watchTournament?.map(item => item?.value), tournamentList?.map(item => item?.id))
            if (!_.isEqual(watchTournament?.map(item => item?.value), tournamentList?.map(item => item?.id)) && formdata?.tournament_id?.length > 0) {
                data.tournaments = formdata?.tournament_id?.map(item => item?.value)
            }
            data.tshirt_details = formdata?.tshirt_details
            setLoading(true)
            const result = await addTShirtService(data);
            if (result?.data?.status) {
                SuccessAlert(result?.data?.message)
                await props?.fetchTShirtList(1);
                await props?.fetchAllTShirtList();
                handleClose()
            } else {
                ErrorAlert(result?.response?.data?.message)
            }
            setLoading(false)
        } catch (error) {
            setLoading(false)
            ErrorAlert(error)
        }
    }

    /**
        * @function handleClose
        * @params
        * @description used to close add t-shirt modal
    */
    const handleClose = async () => {
        reset()
        props.handleClose()
    }

    const reactSelectCustomStyle = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            border: state.isFocused && "1px solid #FA5000",
            boxShadow: state.isFocused && "0px 0px 2px #FA5000",
            "&:hover": {
                border: "1px solid #FA5000",
                boxShadow: "0px 0px 2px #FA5000"
            }
        }),
        option: (styles, { data, isSelected }) => {
            if (data.value === "<SELECT_ALL>" && _.isEqual(watchTournament?.map(item => item?.value), tournamentList?.map(item => item?.id))) {
                return {
                    ...styles,
                    backgroundColor: "#FA5000",
                    color: 'white',
                    "&:hover": {
                        backgroundColor: "#FA5000",
                        color: 'white'
                    }
                }
            } else {
                return {
                    ...styles,
                    backgroundColor: isSelected ? '#FA5000' : 'white',
                    "&:hover": {
                        backgroundColor: "#FA5000",
                        color: 'white'
                    }
                }
            }
        },
        dropdownIndicator: (provided) => ({
            ...provided,
            color: '#CCCCCC'
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            backgroundColor: '#CCCCCC'
        })
    }

    return <Modal show={props.show} onHide={handleClose} size={props.size} centered backdrop="static" keyboard={false}>
        <LoadingOverlay
            active={loading}
            spinner={<Spinner animation="border" />}
        >
            <Modal.Header className='fs-22 fw-bold border-0' closeButton >
                Add T-Shirt
            </Modal.Header>
            <Modal.Body className='d-flex flex-column align-items-center'>
                <Form className='w-100' onSubmit={handleSubmit(onSubmit)}>
                    <Form.Group className="my-4">
                        <Form.Label className='fs-14 fw-500'>T-shirt name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter name here"
                            {...register("tshirt_name", {
                                required: "T-shirt name is required"
                            })}
                        />
                        {errors.tshirt_name && <ErrorText>{errors.tshirt_name.message}</ErrorText>}
                    </Form.Group>

                    <Form.Group className="my-4">
                        <Form.Label className='fs-14 fw-500'>Tournaments</Form.Label>
                        <Controller
                            name={`tournament_id`}
                            control={control}
                            render={({ field }) => (
                                <Select
                                    styles={reactSelectCustomStyle}
                                    placeholder='Select tournaments here'
                                    options={[{
                                        value: "<SELECT_ALL>",
                                        label: "Select All Tournaments"
                                    },
                                    ...tournamentList?.map((item) => {
                                        return {
                                            value: item?.id,
                                            label: item?.name
                                        }
                                    })]}
                                    formatOptionLabel={(data) => {
                                        return <div className='d-flex justify-content-between align-items-center'>
                                            <div className='d-flex align-items-center'>
                                                {tournamentList?.find(item => item?.id === data?.value) &&
                                                    <img className='tournament-banner-small rounded-circle me-2' src={tournamentList?.find(item => item?.id === data?.value)?.banner_image} alt={data.label} />
                                                }
                                                <span>{data.label}</span>
                                            </div>
                                            {watchTournament?.some(item => item?.value === data?.value) &&
                                                <FaCheck />
                                            }
                                        </div>
                                    }}
                                    hideSelectedOptions={false}
                                    isMulti={true}
                                    isSearchable={true}
                                    {...field}
                                    onChange={(selectedOption) => {
                                        if (selectedOption?.some(item => item?.value === "<SELECT_ALL>")) {
                                            field.onChange(tournamentList?.map((item) => {
                                                return {
                                                    value: item?.id,
                                                    label: item?.name
                                                }
                                            }))
                                        } else {
                                            field.onChange(selectedOption)
                                        }
                                    }}
                                />
                            )}
                        />
                    </Form.Group>

                    <div className='mt-3 p-3 border bg-gray rounded'>
                        <div className='d-flex justify-content-between align-items-center'>
                            <span className='fw-600 text-dark'>T-Shirt size and Quantity</span>
                        </div>
                        {fields.map((item, index) => {
                            return (
                                <div className='d-flex flex-wrap align-items-start mt-4 position-relative' key={item.id}>
                                    <div className='d-flex flex-column flex-sm-row flex-sm-wrap align-items-start flex-grow-1'>
                                        <div className='w-xs-100 flex-grow-1 w-xs-100 me-2'>
                                            <Form.Group>
                                                <Form.Label className='fs-14 fw-500'>T-shirt size</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter size"
                                                    {...register(`tshirt_details[${index}].size`, {
                                                        required: "Size is required"
                                                    })}
                                                />
                                                {errors?.tshirt_details?.length > 0 && errors?.tshirt_details[index]?.size && <ErrorText>{errors?.tshirt_details[index]?.size?.message}</ErrorText>}
                                            </Form.Group>
                                        </div>
                                    </div>
                                    <div className='w-xs-100 flex-grow-1 mt-2 mt-sm-0 mx-sm-2'>
                                        <Form.Group>
                                            <Form.Label className='fs-14 fw-500'>Quantity</Form.Label>
                                            <Form.Control
                                                type="number"
                                                placeholder="Enter quantity"
                                                {...register(`tshirt_details[${index}].quantity`, {
                                                    required: "Quantity is required"
                                                })}
                                            />
                                            {errors?.tshirt_details?.length > 0 && errors?.tshirt_details[index]?.quantity && <ErrorText>{errors?.tshirt_details[index]?.quantity?.message}</ErrorText>}
                                        </Form.Group>
                                    </div>

                                    {fields.length > 1 && <IoMdClose role='button' className='position-absolute top-0 end-0' color='red' size={20} onClick={() => remove(index)} />}
                                </div>
                            );
                        })}
                        <div role='button' className='d-inline-flex align-items-center mt-4' onClick={() =>
                            append({
                                size: "",
                                quantity: ""
                            })
                        }>
                            <BsFillPlusCircleFill className='text-primary me-2' size={25} />
                            <span className='text-primary fw-500'>Add more size</span>
                        </div>
                    </div>

                    <div className='d-flex justify-content-end align-items-end mt-4'>
                        <div>
                            <PrimaryButton className='fs-14 fw-500 px-5' disabled={loading} loading={loading} type="submit">Add</PrimaryButton>
                        </div>
                    </div>
                </Form>
            </Modal.Body>
        </LoadingOverlay>
    </Modal>
};

export default AddTShirtModal;